import { memo, useId, useMemo, useRef, useState } from 'react'

import type { EntityType } from '@/api/actions'
import { Input } from '@/components/ui/input'

import { categoryLabel, describeRole, TYPE_LABEL } from '../describe'
import { EnabledSwitch } from '../parts'
import { ClauseList } from './ClausesEditor'
import { useEditor } from './context'
import { asList, asObj, moveItem, newRoleName, removeRole, renameRole, ROLE_RE, setIn, setOrDelete, slugify, uniqueName, type Obj } from './doc'
import { CheckLine, CommitInput, EditField, EditGroup, IssueLines, ItemCard, ListControls, NumberInput, SELECT_CLASS, SectionCard, SelectInput, TextInput } from './fields'
import { ClipsEditor, FallbacksEditor, FxList, ResultsEditor } from './ListsEditors'
import { RoleSpecFields } from './RoleSpecFields'
import { StepsEditor } from './StepsEditor'

/**
 * The manual action editor's form (§9.1 as amended by §11): basics, who takes part, what must be
 * true first, the steps, what is true afterwards, moves with backups, what to try instead, and
 * automatic effects. Everything edits the one JSON document the Advanced tab shows.
 *
 * Each section gets only its part of the document and a stable `update` (a function of the latest
 * document), so typing in one section re-renders that section alone.
 */

const words = (s: string): string => s.replace(/_/g, ' ')

export type Update = (fn: (doc: Obj) => Obj) => void

export interface FormProps {
  doc: Obj
  /** Change the draft: `fn` gets the latest document and returns the next one. */
  update: Update
  mode: 'edit' | 'new'
  /** Ids already used (a new one gets a free id from its name). */
  takenIds: string[]
}

export function ActionForm({ doc, update, mode, takenIds }: FormProps) {
  const on = useMemo(
    () => ({
      preconditions: (v: unknown[]) => update((d) => setIn(d, ['preconditions'], v)),
      program: (v: unknown[]) => update((d) => setIn(d, ['program'], v)),
      result: (v: unknown[]) => update((d) => setIn(d, ['result'], v)),
      clips: (v: Obj) => update((d) => setOrDelete(d, ['clips'], v)),
      fallback: (v: unknown[]) => update((d) => setIn(d, ['fallback'], v)),
      fx_auto: (v: unknown[]) => update((d) => setIn(d, ['fx_auto'], v)),
    }),
    [update],
  )
  const roleNames = Object.keys(asObj(doc.roles)).join(',')
  return (
    <div className="flex flex-col gap-6">
      <ActionBasics
        update={update}
        mode={mode}
        takenIds={takenIds}
        label={doc.label}
        id={doc.id}
        description={doc.description}
        category={doc.category}
        enabled={doc.enabled !== false}
        idea={typeof doc.idea_template === 'string' ? doc.idea_template : ''}
        duration={doc.duration}
        roleNames={roleNames}
      />
      <RolesSection roles={doc.roles} update={update} />
      <PreconditionsSection value={doc.preconditions} onChange={on.preconditions} />
      <ProgramSection value={doc.program} onChange={on.program} />
      <ResultSection value={doc.result} onChange={on.result} />
      <ClipsSection value={doc.clips} onChange={on.clips} />
      <FallbackSection value={doc.fallback} onChange={on.fallback} />
      <FxAutoSection value={doc.fx_auto} onChange={on.fx_auto} />
    </div>
  )
}

interface ListSectionProps {
  value: unknown
  onChange(v: unknown[]): void
}

const PreconditionsSection = memo(function PreconditionsSection({ value, onChange }: ListSectionProps) {
  return (
    <SectionCard path="preconditions" title="What must be true first" help="Checks made before it can happen. When one fails, the story tries what is under “If it can’t happen”." testId="section-preconditions">
      <ClauseList path="preconditions" clauses={asList(value)} onChange={onChange} />
    </SectionCard>
  )
})

const ProgramSection = memo(function ProgramSection({ value, onChange }: ListSectionProps) {
  return (
    <SectionCard path="program" title="Step by step" help="Steps happen one after another; inside a step everyone moves at the same time." testId="section-program">
      <StepsEditor path="program" steps={asList(value)} onChange={onChange} atLeastOne />
    </SectionCard>
  )
})

const ResultSection = memo(function ResultSection({ value, onChange }: ListSectionProps) {
  return (
    <SectionCard path="result" title="Afterwards" help="What is true once it has happened: who holds what, who is wet, how someone feels." testId="section-result">
      <ResultsEditor path="result" items={asList(value)} onChange={onChange} />
    </SectionCard>
  )
})

const ClipsSection = memo(function ClipsSection({ value, onChange }: { value: unknown; onChange(v: Obj): void }) {
  return (
    <SectionCard path="clips" title="Moves and backups" help="If a puppet doesn’t know a move, it tries the next one on the list." testId="section-clips">
      <ClipsEditor clips={asObj(value)} onChange={onChange} />
    </SectionCard>
  )
})

const FallbackSection = memo(function FallbackSection({ value, onChange }: ListSectionProps) {
  return (
    <SectionCard path="fallback" title="If it can’t happen" help="Try another action instead, in this order." testId="section-fallback">
      <FallbacksEditor items={asList(value)} onChange={onChange} />
    </SectionCard>
  )
})

const FxAutoSection = memo(function FxAutoSection({ value, onChange }: ListSectionProps) {
  return (
    <SectionCard path="fx_auto" title="Automatic effects" help="Sparkles, hearts, splashes… added at a moment of the action (a move’s name, or an event like ball:arrive)." testId="section-fx_auto">
      <FxList path="fx_auto" auto items={asList(value)} onChange={onChange} />
    </SectionCard>
  )
})

/* -------------------------------------------------------------------------- */
/* Basics                                                                      */
/* -------------------------------------------------------------------------- */

interface BasicsProps {
  update: Update
  mode: 'edit' | 'new'
  takenIds: string[]
  label: unknown
  id: unknown
  description: unknown
  category: unknown
  enabled: boolean
  idea: string
  duration: unknown
  /** Participant names, comma-joined (a string keeps the memo cheap). */
  roleNames: string
}

const ActionBasics = memo(function ActionBasics({ update, mode, takenIds, label, id, description, category, enabled, idea, duration, roleNames }: BasicsProps) {
  const { meta } = useEditor()
  const [idByHand, setIdByHand] = useState(false)
  const roles = roleNames ? roleNames.split(',') : []
  const categoryField = (
    <EditField path="category" label="Kind of action">
      {(ctrl) => <SelectInput ctrl={ctrl} value={typeof category === 'string' ? category : 'play'} options={meta.categories.map((c) => ({ value: c, label: categoryLabel(c) }))} onChange={(v) => update((d) => setOrDelete(d, ['category'], v))} />}
    </EditField>
  )
  return (
    <SectionCard path="" title="Basics" help="What it is called and how a story talks about it." testId="section-basics">
      {/* The id can't change once saved; a problem with it (changed in the JSON) shows here. */}
      {mode === 'edit' ? <IssueLines path="id" /> : null}
      <div className="grid gap-4 md:grid-cols-2">
        <EditField path="label" label="Name" hint="Short, like “Throw” or “Share a snack”.">
          {(ctrl) => (
            <TextInput
              ctrl={ctrl}
              value={label}
              placeholder="Juggle"
              onChange={(v) =>
                update((d) => {
                  const next = setIn(d, ['label'], v)
                  return mode === 'new' && !idByHand ? setIn(next, ['id'], uniqueName(slugify(v), takenIds)) : next
                })
              }
            />
          )}
        </EditField>
        {mode === 'new' ? (
          <EditField path="id" label="Saved as" hint="Letters, numbers and _ — made from the name. It can’t change later.">
            {(ctrl) => (
              <TextInput
                ctrl={ctrl}
                mono
                value={id}
                onChange={(v) => {
                  setIdByHand(true)
                  update((d) => setIn(d, ['id'], v.toLowerCase().replace(/\s+/g, '_')))
                }}
              />
            )}
          </EditField>
        ) : (
          categoryField
        )}
        <EditField path="description" label="In one line" hint="What happens — the AI reads this too." className="md:col-span-2">
          {(ctrl) => <TextInput ctrl={ctrl} value={description} placeholder="throw something to a friend, who catches it" onChange={(v) => update((d) => setIn(d, ['description'], v))} />}
        </EditField>
        {mode === 'new' ? categoryField : null}
        <EnabledField checked={enabled} onChange={(v) => update((d) => setIn(d, ['enabled'], v))} />
        <IdeaTemplateField value={idea} roles={roles} onChange={(v) => update((d) => setOrDelete(d, ['idea_template'], v))} />
        <DurationFields duration={asObj(duration)} update={update} />
      </div>
    </SectionCard>
  )
})

export function EnabledField({ checked, onChange }: { checked: boolean; onChange(v: boolean): void }) {
  return (
    <div className="flex items-center gap-3 self-end rounded-xl border border-line p-3" data-field-path="enabled">
      <EnabledSwitch checked={checked} onChange={onChange} label="Use it in stories" />
      <span className="flex flex-col text-sm">
        <span className="font-medium text-ink">{checked ? 'Used in stories' : 'Switched off'}</span>
        <span className="text-xs text-ink-2">{checked ? 'New stories may use it.' : 'New stories leave it out.'}</span>
      </span>
    </div>
  )
}

/** `idea_template`: how a story line says it, with a chip per participant that inserts `{name}`. */
function IdeaTemplateField({ value, roles, onChange }: { value: string; roles: string[]; onChange(v: string): void }) {
  const ref = useRef<HTMLInputElement>(null)
  const insert = (role: string): void => {
    const el = ref.current
    const token = `{${role}}`
    if (!el) return onChange(`${value}${token}`)
    const start = el.selectionStart ?? value.length
    const end = el.selectionEnd ?? value.length
    onChange(`${value.slice(0, start)}${token}${value.slice(end)}`)
    setTimeout(() => {
      el.focus()
      el.setSelectionRange(start + token.length, start + token.length)
    }, 0)
  }
  // "{actor} throws the {object}" reads "the actor throws the object" (no "the the").
  const reading = value.replace(/(\b(?:the|a|an|his|her|its|their)\s+)?\{([a-z][a-z0-9_]*)\}/gi, (_m, article: string | undefined, r: string) => `${article ?? 'the '}${words(r)}`)
  return (
    <EditField path="idea_template" label="How a story says it" className="md:col-span-2" hint={value ? `Reads: “${reading}”` : 'For example: {thrower} throws the {ball} to {catcher}.'}>
      {(ctrl) => (
        <div className="flex flex-col gap-2">
          <Input
            ref={ref}
            id={ctrl.id}
            fieldSize="sm"
            value={value}
            aria-invalid={ctrl.invalid || undefined}
            aria-describedby={ctrl.describedBy}
            placeholder="{actor} waves at {friend}"
            onChange={(e) => onChange(e.target.value)}
          />
          {roles.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-ink-3">Put in:</span>
              {roles.map((r) => (
                <button key={r} type="button" className="h-7 rounded-full border border-line bg-paper px-2.5 font-mono text-xs text-ink-2 hover:bg-paper-2 hover:text-ink focus-visible:focus-ring" onClick={() => insert(r)} aria-label={`Put in the ${words(r)}`}>
                  {`{${r}}`}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </EditField>
  )
}

function DurationFields({ duration, update }: { duration: Obj; update: Update }) {
  const set = (key: string, v: number | undefined): void => update((d) => setOrDelete(d, ['duration'], setOrDelete(asObj(d.duration), [key], v)))
  return (
    <EditGroup path="duration" label="How long it takes (seconds)" hint="The story keeps it between the shortest and the longest." className="md:col-span-2">
      <div className="grid grid-cols-3 gap-3">
        <EditField path="duration.min" label="Shortest">
          {(ctrl) => <NumberInput ctrl={ctrl} value={duration.min} min={0.1} max={60} step={0.1} placeholder="1" onChange={(v) => set('min', v)} />}
        </EditField>
        <EditField path="duration.default" label="Usually">
          {(ctrl) => <NumberInput ctrl={ctrl} value={duration.default} min={0.1} max={60} step={0.1} placeholder="2" onChange={(v) => set('default', v)} />}
        </EditField>
        <EditField path="duration.max" label="Longest">
          {(ctrl) => <NumberInput ctrl={ctrl} value={duration.max} min={0.1} max={60} step={0.1} placeholder="4" onChange={(v) => set('max', v)} />}
        </EditField>
      </div>
    </EditGroup>
  )
}

/* -------------------------------------------------------------------------- */
/* Roles                                                                       */
/* -------------------------------------------------------------------------- */

const RolesSection = memo(function RolesSection({ roles: raw, update }: { roles: unknown; update: Update }) {
  const { meta } = useEditor()
  const roles = asObj(raw)
  const names = Object.keys(roles)
  const addId = useId()
  const reorder = (i: number, by: -1 | 1): void => {
    const order = moveItem(names, i, by)
    update((d) => {
      const current = asObj(d.roles)
      return setIn(d, ['roles'], Object.fromEntries(order.map((n) => [n, current[n]])))
    })
  }
  return (
    <SectionCard path="roles" title="Who takes part" help="Characters, things, scenery, places or spots. Each one gets a short name the steps use (like $thrower)." testId="section-roles">
      <div className="flex flex-col gap-3">
        {names.map((name, i) => (
          <RoleCard
            key={name}
            name={name}
            spec={asObj(roles[name])}
            index={i}
            count={names.length}
            taken={names}
            onRename={(to) => update((d) => renameRole(d, name, to))}
            onChange={(spec) => update((d) => setIn(d, ['roles', name], spec))}
            onMove={(by) => reorder(i, by)}
            onRemove={() => update((d) => removeRole(d, name))}
          />
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor={addId} className="text-sm text-ink-2">
            Add a participant:
          </label>
          <select
            id={addId}
            className={`${SELECT_CLASS} w-auto`}
            value=""
            data-testid="add-role"
            disabled={names.length >= 6}
            onChange={(e) => {
              const type = e.target.value
              if (!type) return
              update((d) => setIn(d, ['roles', newRoleName(type, Object.keys(asObj(d.roles)))], { types: [type], required: true }))
            }}
          >
            <option value="">{names.length >= 6 ? 'Six is the most' : 'Choose what kind…'}</option>
            {meta.entity_types.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t as EntityType] ?? words(t)}
              </option>
            ))}
          </select>
        </div>
      </div>
    </SectionCard>
  )
})

function RoleCard({
  name,
  spec,
  index,
  count,
  taken,
  onRename,
  onChange,
  onMove,
  onRemove,
}: {
  name: string
  spec: Obj
  index: number
  count: number
  taken: string[]
  onRename(to: string): void
  onChange(spec: Obj): void
  onMove(by: -1 | 1): void
  onRemove(): void
}) {
  const summary = describeRole(name, spec)
  return (
    <ItemCard
      path={`roles.${name}`}
      testId="role"
      title={
        <span className="flex min-w-0 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-ink-3">$</span>
            <CommitInput
              label={`Name of participant ${index + 1}`}
              value={name}
              className="w-44"
              testId="role-name"
              accept={(to) => ROLE_RE.test(to) && !taken.includes(to)}
              rejectText="Use 2–25 letters, numbers or _, starting with a letter, and not a name already used."
              onCommit={onRename}
            />
          </span>
          <span className="text-xs font-normal text-ink-2" data-testid="role-reading">
            {summary.sentence}
          </span>
        </span>
      }
      controls={<ListControls name={`the ${words(name)}`} index={index} count={count} onMove={onMove} onRemove={onRemove} removeDisabled={count <= 1} />}
    >
      <CheckLine path={`roles.${name}.required`} label="Must be there" hint="Off: the action also works without it." checked={spec.required !== false} onChange={(v) => onChange({ ...spec, required: v })} />
      <RoleSpecFields path={`roles.${name}`} spec={spec} who={`the ${words(name)}`} onChange={onChange} />
    </ItemCard>
  )
}
